import { useEffect, useState } from 'react'
import { sendSearchQueryEvent } from 'src/sdk/analytics/platform/intelligentSearch'
import { buildProductListObject } from 'src/utils/restructure/analytics/amplitude/buildProductListObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { OptionsMap } from 'src/components/search/SortV2/Sort'
import type {
  Facet,
  SearchSort,
} from '@faststore/sdk/dist/search/useSearchState'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { PAGE_TYPE_MAP } from 'src/hooks/usePageType'

import type { SearchResult } from '../types'
import type { GalleryType } from '../ProductGalleryv3/ProductGalleryv3'

type UseProductGalleryAnalyticsProps = {
  searchResult: SearchResult | undefined
  term: string | null
  type: GalleryType
  title: string
  totalCount: number | undefined
  page: number
  sort: string
  selectedFacets: Facet[]
  isSearchPage: boolean
  base?: string
  oldSort?: SearchSort
}

export const useProductGalleryAnalytics = ({
  searchResult,
  term,
  type,
  title,
  totalCount,
  page,
  sort,
  oldSort,
  selectedFacets,
  isSearchPage,
  base,
}: UseProductGalleryAnalyticsProps) => {
  const [lastTerm, setLastTerm] = useState<null | string>(null)
  const { pageData } = useGlobalContext()

  const getPageTitle = (str: string) => str?.replace(/-/g, ' ') ?? ''

  useEffect(() => {
    if (!searchResult || typeof window === 'undefined') {
      return
    }

    if (isSearchPage) {
      sendSearchQueryEvent({
        name: 'intelligent_search_query',
        params: {
          url: window.location.href,
          results: searchResult.filteredRecords,
        },
      })
    }

    const productListObj = buildProductListObject({
      listType: pageData.currentPageType,
      listName: `Página de lista de produtos: ${getPageTitle(title)}`,
      productsCount: searchResult?.filteredRecords ?? 0,
      sort: OptionsMap[sort as keyof typeof OptionsMap],
      selectedFacets,
      page: page === 0 ? 1 : page,
      base,
    })

    dispatchAmplitudeEvent({
      eventName: 'Product List Viewed',
      eventData: {
        ...getStandardObject(pageData),
        ...productListObj,
      },
    })

    setLastTerm(term)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult, term, page, base, pageData])

  useEffect(() => {
    if (pageData.currentPageType !== PAGE_TYPE_MAP.Pesquisa) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Search Results Viewed',
      eventData: {
        ...getStandardObject(pageData),
        'search term': title,
        'number search results': totalCount,
      },
    })
  }, [pageData, title, totalCount])

  useEffect(() => {
    if (
      pageData.currentPageType !== PAGE_TYPE_MAP.Pesquisa ||
      oldSort !== sort ||
      term === lastTerm
    ) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Search Completed',
      eventData: {
        ...getStandardObject(pageData),
        'search term': title,
        'number search results': totalCount,
      },
    })
  }, [oldSort, pageData, sort, title, totalCount, type, term, lastTerm])

  return {
    lastTerm,
  }
}
